import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DimmingSettings {
  mode: string;
  startHour: number;
  endHour: number;
}

interface NightModeOverlayProps {
  dimmingSettings: DimmingSettings;
  children: React.ReactNode;
}

const NightModeOverlay: React.FC<NightModeOverlayProps> = ({ dimmingSettings, children }) => {
  const [isNightMode, setIsNightMode] = useState(false);

  useEffect(() => {
    const checkNightMode = () => {
      try {
        if (!dimmingSettings || dimmingSettings.mode === 'off') {
          setIsNightMode(false);
          return;
        }

        // Validate settings
        if (!['off', 'dimmed', 'blackout'].includes(dimmingSettings.mode)) {
          console.warn('Invalid dimming mode, defaulting to off:', dimmingSettings.mode);
          setIsNightMode(false);
          return;
        }

        if (dimmingSettings.startHour < 0 || dimmingSettings.startHour > 23 ||
            dimmingSettings.endHour < 0 || dimmingSettings.endHour > 23) {
          console.warn('Invalid hour values, defaulting to off:', dimmingSettings);
          setIsNightMode(false);
          return;
        }

        const now = dayjs();
        const currentHour = now.hour();
        
        // Handle night mode spanning midnight (e.g., 22:00 to 06:00)
        const isInNightPeriod = dimmingSettings.endHour < dimmingSettings.startHour
          ? currentHour >= dimmingSettings.startHour || currentHour < dimmingSettings.endHour
          : currentHour >= dimmingSettings.startHour && currentHour < dimmingSettings.endHour;
        
        setIsNightMode(isInNightPeriod);
        
        // Simple console log only when dimming is enabled
        if (dimmingSettings.mode !== 'off') {
          console.log(`🌙 Dimming: ${dimmingSettings.mode} | Schedule: ${dimmingSettings.startHour}:00-${dimmingSettings.endHour}:00 | Current: ${currentHour}:00 | Active: ${isInNightPeriod}`);
        }
      } catch (error) {
        console.error('Error checking night mode:', error);
        setIsNightMode(false);
      }
    };

    // Check immediately
    checkNightMode();

    // Check every minute
    const interval = setInterval(checkNightMode, 60000);

    return () => clearInterval(interval);
  }, [dimmingSettings]);

  if (!isNightMode || dimmingSettings.mode === 'off') {
    return <>{children}</>;
  }

  if (dimmingSettings.mode === 'blackout') {
    return (
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Optional: Add a small logo or company branding */}
        <div style={{
          opacity: 0.1,
          fontSize: '2rem',
          color: '#333',
          textAlign: 'center'
        }}>
          🌙
        </div>
      </div>
    );
  }

  if (dimmingSettings.mode === 'dimmed') {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
        {children}
        {/* Dark overlay with very low opacity content */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.85)', // 85% dark overlay
          pointerEvents: 'none',
          zIndex: 1000,
          filter: 'blur(1px)', // Slight blur for softer effect
        }} />
        {/* Additional filter effects on the content */}
        <style>{`
          .dimmed-content {
            filter: brightness(0.15) contrast(0.7) saturate(0.3);
          }
        `}</style>
      </div>
    );
  }

  return <>{children}</>;
};

export default NightModeOverlay; 